import React, { useEffect, useState } from 'react';
import styled from 'App/styled';
import { useDispatch, useSelector } from 'react-redux';
import { noIndexNoFollowContent } from '@nykaa/seo/constants';
import { pushEvent } from '@nykaa/data-layer';
import appEvents from 'store/app/actionTypes';
import MetaInfo from './MetaInfo';
import Loading from 'components/Loader';
import Error from 'shared/components/Error';
import CustomWidgetRenderer from 'shared/components/WidgetRenderer';
import DownloadAppBanner from 'components/DownloadAppBanner';

import BelowTheFold from './belowTheFold';
import { getAppTheme } from 'shared/App/theme';
import { getPageConfigInventory } from './helper';
import { addElementIfNotExists } from 'shared/helpers/utils/Array';

import PageTypes from 'constants/pageTypes';
import { TRACKING_PAGE_TYPES } from 'components/CartOperations/constants';
import { State } from 'store/types';
import { setCartNudgeStatus } from 'store/app/actions';
import { NUDGE_DEFAULT_POSITION } from 'shared/constants';
import { isDeviceNotWeb } from 'helpers/utils/device';
import dealsUtils from '@nykaa/deals-utils/basic';

const Shimmer = styled(Loading)`
  width: 100%;
  margin-bottom: 10px;
`;

const Wrapper = styled.div`
  padding: 0;
`;

const Title = styled.h1`
  background-color: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.textPrimary};
  margin: ${({ theme }) => theme.spacing.spacing0};
  padding: ${({ theme }) =>
    `${theme.spacing.spacing20} ${theme.spacing.spacing80} ${theme.spacing.spacing60}`};
  ${({ theme }) => theme.typography.titleSmall};
`;

const NLP_LOAD_EVENT = 'native_landing_page_loads';
interface Props {
  isFetchingError: boolean;
  widgets: any[];
  loading: boolean;
  configs: {
    initialInventories: number;
    eagerLoadImages: number;
  };
  pageName: string;
  loadInterFont: boolean;
  isPaginationEnabled?: boolean;
  remainingInventoryIds?: string[];
  queryParamsObject?: object;
  deIndex?: boolean;
}

const View = ({
  widgets,
  loading,
  configs,
  isFetchingError,
  pageName,
  loadInterFont,
  isPaginationEnabled,
  remainingInventoryIds,
  queryParamsObject,
  deIndex,
}: Props) => {
  // state and dispatch functions
  const dispatch = useDispatch();
  const PageName =
    pageName === PageTypes.HOME
      ? TRACKING_PAGE_TYPES.HOMEPAGE
      : TRACKING_PAGE_TYPES.NLP;
  const [currentBatchLoading, setCurrentBatchLoading] = useState(false);
  const trackPageName =
    useSelector((state: State) => state.dataLayer?.pageName) || '';
  const title =
    useSelector((state: State) => state.remoteConfig?.configs?.homeh1Tag) || '';
  const device = useSelector((state: State) => state.appReducer?.client.device);
  const currentBatchLoadingCb = (isLoading: boolean) => {
    setCurrentBatchLoading(isLoading);
  };

  useEffect(() => {
    window.requestIdleCallback(() => {
      if (PageName === TRACKING_PAGE_TYPES.NLP) {
        const eventProperties = {
          page: trackPageName,
          page_type: 'nativeLandingPage',
          interaction_location: '',
        };
        const queryParams = new URLSearchParams(location.search);
        const intcmp = queryParams.get('intcmp');
        if (intcmp && intcmp.includes('mweb-homepage'))
          eventProperties.interaction_location = 'homePage';
        pushEvent(NLP_LOAD_EVENT, eventProperties);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    dispatch({
      type: appEvents.APP_REDUCER_SET_PAGE,
      payload: pageName,
    });
  }, [dispatch, pageName]);

  /**
   * Dispatches an action to show cart nudge for Mweb devices
   */
  useEffect(() => {
    const isNotWeb = isDeviceNotWeb(device);
    !isNotWeb && dispatch(setCartNudgeStatus(true, NUDGE_DEFAULT_POSITION));
  }, [device, dispatch]);

  if (isFetchingError) {
    return <Error />;
  }

  if (loading) {
    return (
      <div>
        {[0, 1, 2].map((item) => (
          <Shimmer
            className="client"
            key={item}
          />
        ))}
      </div>
    );
  }

  if (!widgets || widgets.length < 1) {
    return null;
  }
  const { initialInventories, eagerLoadImages } = configs || {};
  const appConfigWidget = getPageConfigInventory(widgets);
  const widgetsToRender = addElementIfNotExists(
    widgets.slice(0, initialInventories),
    appConfigWidget
  );
  const btfWidgets = addElementIfNotExists(
    widgets.slice(initialInventories),
    appConfigWidget
  );
  // if all widgets in btf are empty
  const emptyWidgetsNew = btfWidgets.reduce((acc: number[], widget: any) => {
    if (dealsUtils.isEmptyChildWidget(widget?.widgetData)) {
      acc.push(widget?.position); // +1 to match the 1-based index
    }
    return acc;
  }, []);
  const isAllEmpyWidgets = emptyWidgetsNew.length === btfWidgets.length;
  const metaRobots = deIndex ? noIndexNoFollowContent : '';
  const pageData =
    widgets && widgets.length > 0 ? widgets[0].inventoryPageData : 'default';
  return (
    <>
      <MetaInfo
        loadInterFont={loadInterFont}
        metaRobots={metaRobots}
      />
      <Wrapper>
        <CustomWidgetRenderer
          widgetList={widgetsToRender}
          isServer={__SERVER__}
          theme={getAppTheme()}
          pageName={PageName}
          eagerLoadImages={eagerLoadImages}
          eagerLoadInventories={initialInventories}
          allWidgetsLength={widgets?.length || 0}
          pageData={pageData}
        />
        <BelowTheFold
          pageName={PageName}
          widgets={btfWidgets}
          allWidgetsLength={widgets?.length || 0}
          remainingInventoryIds={remainingInventoryIds}
          queryParamsObject={queryParamsObject}
          pageData={pageData}
          currentBatchLoadingCb={currentBatchLoadingCb}
          noImpressionImmediatePaginationCall={
            isPaginationEnabled &&
            (isAllEmpyWidgets || btfWidgets?.length === 0)
          }
        />
        {title && <Title>{title}</Title>}
        <DownloadAppBanner />
      </Wrapper>
      {isPaginationEnabled && currentBatchLoading && (
        <Shimmer className="client" />
      )}
    </>
  );
};

export default View;
